// PDF Template types for frontend

export type PdfElementKind =
  | 'logo'
  | 'productImage'
  | 'textField'
  | 'staticText'
  | 'richText'
  | 'rectangle'
  | 'line'
  | 'staticImage'
  | 'bulletList';

export interface IPdfElementStyle {
  fontFamily?: 'Kapra Neue' | 'Arial' | 'Oswald';
  fontSize?: number;
  fontWeight?: number | 'normal' | 'bold' | 'semibold';
  fontStyle?: 'normal' | 'italic';
  color?: string;
  backgroundColor?: string;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: 'solid' | 'dashed' | 'dotted';
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  padding?: number;
  opacity?: number;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing?: number;
  lineHeight?: number;
  bulletStyle?: 'disc' | 'circle' | 'square' | 'none';
  autoFit?: boolean;
  minFontSize?: number; // pt
  imageAlign?: 'top' | 'center' | 'bottom';
}

export interface IPdfElementBinding {
  metadataKey?: string; // Optional - allows saving templates without selecting a field
  fallbackValue?: string;
  prefix?: string;
  suffix?: string;
}

export interface IPdfElement {
  id: string;
  kind: PdfElementKind;
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  style: IPdfElementStyle;
  binding?: IPdfElementBinding;
  content?: string;
  imageUrl?: string; // For staticImage element - URL/path to image
  zIndex?: number;
  rotation?: number;
  locked?: boolean;
}

export interface IPdfPageSettings {
  size: 'A4';
  orientation: 'portrait' | 'landscape';
  marginMm: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  backgroundColor?: string;
}

// Template mode - single product or multi-product
export type TemplateMode = 'single' | 'multi';

// Layout presets for multi-product templates
export type LayoutPreset = '3x2' | '3x3' | '3x4' | '4x4' | 'custom';

// Layout configuration for multi-product templates
export interface IMultiProductLayout {
  preset: LayoutPreset;
  rows: number;
  cols: number;
  gapMm?: number;
}

// A product slot - where a product block will be placed
export interface IProductSlot {
  id: string;
  blockTemplateId?: string;
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
}

export interface IPdfTemplate {
  _id?: string;
  type: 'products' | 'wine';
  name: string;
  description?: string;
  isActive: boolean;
  showInAssetMenus: boolean;
  order: number;
  page: IPdfPageSettings;
  elements: IPdfElement[];
  
  // Multi-product mode fields
  templateMode?: TemplateMode;
  layout?: IMultiProductLayout;
  productSlots?: IProductSlot[];
  maxProducts?: number;
  
  createdAt?: string;
  updatedAt?: string;
}

// A4 dimensions in mm
export const A4_WIDTH_MM = 210;
export const A4_HEIGHT_MM = 297;

// Default page settings
export const DEFAULT_PAGE_SETTINGS: IPdfPageSettings = {
  size: 'A4',
  orientation: 'portrait',
  marginMm: { top: 10, right: 10, bottom: 10, left: 10 },
  backgroundColor: '#ffffff',
};

// Default template
export const DEFAULT_PDF_TEMPLATE: Omit<IPdfTemplate, '_id'> = {
  type: 'products',
  name: '',
  description: '',
  isActive: true,
  showInAssetMenus: true,
  order: 0,
  page: DEFAULT_PAGE_SETTINGS,
  elements: [],
  templateMode: 'single',
};

// Layout presets with their configurations
export const LAYOUT_PRESETS: Record<LayoutPreset, { rows: number; cols: number; label: string }> = {
  '3x2': { rows: 3, cols: 2, label: '3 rader × 2 kolumner (6 produkter)' },
  '3x3': { rows: 3, cols: 3, label: '3 rader × 3 kolumner (9 produkter)' },
  '3x4': { rows: 3, cols: 4, label: '3 rader × 4 kolumner (12 produkter)' },
  '4x4': { rows: 4, cols: 4, label: '4 rader × 4 kolumner (16 produkter)' },
  'custom': { rows: 1, cols: 1, label: 'Anpassad layout' },
};

// Element presets for quick adding
export const ELEMENT_PRESETS: Record<PdfElementKind, Partial<IPdfElement>> = {
  logo: {
    kind: 'logo',
    xMm: 10,
    yMm: 10,
    wMm: 50,
    hMm: 20,
    style: {},
  },
  productImage: {
    kind: 'productImage',
    xMm: 10,
    yMm: 40,
    wMm: 80,
    hMm: 80,
    style: {},
  },
  textField: {
    kind: 'textField',
    xMm: 100,
    yMm: 40,
    wMm: 90,
    hMm: 10,
    style: {
      fontFamily: 'Arial',
      fontSize: 12,
      color: '#000000',
    },
    binding: {
      metadataKey: '',
      fallbackValue: '',
    },
  },
  staticText: {
    kind: 'staticText',
    xMm: 10,
    yMm: 130,
    wMm: 100,
    hMm: 10,
    style: {
      fontFamily: 'Arial',
      fontSize: 12,
      color: '#000000',
    },
    content: 'Text här...',
  },
  richText: {
    kind: 'richText',
    xMm: 10,
    yMm: 130,
    wMm: 120,
    hMm: 30,
    style: {
      fontFamily: 'Arial',
      fontSize: 12,
      color: '#000000',
      lineHeight: 1.3,
    },
    content: `<span style="font-family: 'Kapra Neue'; font-size: 18pt; color: #1B3967;">{{title}}</span><br/>` +
      `<span style="font-size: 10pt; color: #333333;">Art.nr: {{artikelnummer}}</span>`,
  },
  rectangle: {
    kind: 'rectangle',
    xMm: 10,
    yMm: 150,
    wMm: 50,
    hMm: 30,
    style: {
      backgroundColor: '#f0f0f0',
      borderWidth: 0.5,
      borderColor: '#cccccc',
    },
  },
  line: {
    kind: 'line',
    xMm: 10,
    yMm: 190,
    wMm: 190,
    hMm: 0,
    style: {
      borderWidth: 0.5,
      borderColor: '#000000',
      borderStyle: 'solid',
    },
  },
  staticImage: {
    kind: 'staticImage',
    xMm: 10,
    yMm: 10,
    wMm: 100,
    hMm: 30,
    style: {},
    imageUrl: '',
  },
  bulletList: {
    kind: 'bulletList',
    xMm: 10,
    yMm: 100,
    wMm: 80,
    hMm: 40,
    style: {
      fontFamily: 'Arial',
      fontSize: 10,
      color: '#000000',
      bulletStyle: 'disc',
    },
    content: '• Punkt ett\n• Punkt två\n• Punkt tre',
  },
};

// Kind labels for UI
export const ELEMENT_KIND_LABELS: Record<PdfElementKind, string> = {
  logo: 'Logo',
  productImage: 'Produktbild',
  textField: 'Datafält',
  staticText: 'Statisk text',
  richText: 'Textblock (rik formatering)',
  rectangle: 'Rektangel',
  line: 'Linje',
  staticImage: 'Header/Banner bild',
  bulletList: 'Punktlista',
};

// Font options
export const FONT_OPTIONS = [
  { value: 'Kapra Neue', label: 'Kapra Neue (Rubrik)' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Oswald', label: 'Oswald' },
];

// Font weight options
export const FONT_WEIGHT_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Fet' },
  { value: 'semibold', label: 'Halvfet' },
];

// Font style options
export const FONT_STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'italic', label: 'Kursiv' },
];

// Text align options
export const TEXT_ALIGN_OPTIONS = [
  { value: 'left', label: 'Vänster' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Höger' },
];

// Vertical align options
export const VERTICAL_ALIGN_OPTIONS = [
  { value: 'top', label: 'Topp' },
  { value: 'middle', label: 'Mitten' },
  { value: 'bottom', label: 'Botten' },
];

// Text transform options
export const TEXT_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'Ingen' },
  { value: 'uppercase', label: 'VERSALER' },
  { value: 'lowercase', label: 'gemener' },
  { value: 'capitalize', label: 'Stort Första' },
];

// Border style options (for lines)
export const BORDER_STYLE_OPTIONS = [
  { value: 'solid', label: 'Heldragen' },
  { value: 'dashed', label: 'Streckad' },
  { value: 'dotted', label: 'Prickad' },
];

// Bullet style options
export const BULLET_STYLE_OPTIONS = [
  { value: 'disc', label: '● Fylld cirkel' },
  { value: 'circle', label: '○ Tom cirkel' },
  { value: 'square', label: '■ Fyrkant' },
  { value: 'none', label: 'Ingen' },
];

// Fontana brand colors
export const FONTANA_COLORS = {
  darkBlue: '#1B3967',
  lightBlue: '#4A7BA7',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  darkGray: '#333333',
};

// Pre-made professional templates
export const PRESET_TEMPLATES: Array<{ name: string; description: string; template: Omit<IPdfTemplate, '_id'> }> = [
  {
    name: 'Produktblad Standard',
    description: 'Klassiskt produktblad med bild till vänster och info till höger',
    template: {
      type: 'products',
      name: 'Produktblad Standard',
      description: 'Klassiskt produktblad med bild till vänster och information till höger',
      isActive: true,
      showInAssetMenus: true,
      order: 0,
      page: {
        size: 'A4',
        orientation: 'portrait',
        marginMm: { top: 0, right: 0, bottom: 0, left: 0 },
        backgroundColor: '#FFFFFF',
      },
      elements: [
        // Header bar (dark blue)
        {
          id: 'header-bar',
          kind: 'rectangle',
          xMm: 0,
          yMm: 0,
          wMm: 210,
          hMm: 12,
          style: { backgroundColor: '#1B3967' },
          zIndex: 0,
        },
        // Product image
        {
          id: 'product-image',
          kind: 'productImage',
          xMm: 15,
          yMm: 25,
          wMm: 75,
          hMm: 100,
          style: {},
          zIndex: 1,
        },
        // Product name (title)
        {
          id: 'product-name',
          kind: 'textField',
          xMm: 100,
          yMm: 25,
          wMm: 95,
          hMm: 15,
          style: {
            fontFamily: 'Kapra Neue',
            fontSize: 24,
            fontWeight: 'bold',
            color: '#1B3967',
            textTransform: 'uppercase',
          },
          binding: { metadataKey: 'title', fallbackValue: 'Produktnamn' },
          zIndex: 2,
        },
        // Description
        {
          id: 'description',
          kind: 'textField',
          xMm: 100,
          yMm: 42,
          wMm: 95,
          hMm: 50,
          style: {
            fontFamily: 'Arial',
            fontSize: 10,
            color: '#333333',
            lineHeight: 1.4,
          },
          binding: { metadataKey: 'produktbeskrivning', fallbackValue: 'Produktbeskrivning' },
          zIndex: 2,
        },
        // Separator line
        {
          id: 'separator',
          kind: 'line',
          xMm: 100,
          yMm: 95,
          wMm: 95,
          hMm: 0,
          style: { borderWidth: 0.3, borderColor: '#CCCCCC' },
          zIndex: 2,
        },
        // Article number label
        {
          id: 'article-label',
          kind: 'staticText',
          xMm: 100,
          yMm: 100,
          wMm: 30,
          hMm: 6,
          style: {
            fontFamily: 'Arial',
            fontSize: 8,
            color: '#666666',
          },
          content: 'Art.nr:',
          zIndex: 2,
        },
        // Article number value
        {
          id: 'article-number',
          kind: 'textField',
          xMm: 130,
          yMm: 100,
          wMm: 65,
          hMm: 6,
          style: {
            fontFamily: 'Arial',
            fontSize: 8,
            fontWeight: 'bold',
            color: '#333333',
          },
          binding: { metadataKey: 'artikelnummer', fallbackValue: '' },
          zIndex: 2,
        },
        // Footer bar (dark blue)
        {
          id: 'footer-bar',
          kind: 'rectangle',
          xMm: 0,
          yMm: 267,
          wMm: 210,
          hMm: 30,
          style: { backgroundColor: '#1B3967' },
          zIndex: 0,
        },
        // Logo in footer
        {
          id: 'logo',
          kind: 'logo',
          xMm: 150,
          yMm: 272,
          wMm: 50,
          hMm: 20,
          style: {},
          zIndex: 3,
        },
      ],
    },
  },
  {
    name: 'Produktblad Kompakt',
    description: 'Kompakt layout med mer utrymme för produktinformation',
    template: {
      type: 'products',
      name: 'Produktblad Kompakt',
      description: 'Kompakt layout med produktinfo i fokus',
      isActive: true,
      showInAssetMenus: true,
      order: 1,
      page: {
        size: 'A4',
        orientation: 'portrait',
        marginMm: { top: 0, right: 0, bottom: 0, left: 0 },
        backgroundColor: '#FFFFFF',
      },
      elements: [
        // Left sidebar (dark blue)
        {
          id: 'sidebar',
          kind: 'rectangle',
          xMm: 0,
          yMm: 0,
          wMm: 70,
          hMm: 297,
          style: { backgroundColor: '#1B3967' },
          zIndex: 0,
        },
        // Product image on sidebar
        {
          id: 'product-image',
          kind: 'productImage',
          xMm: 5,
          yMm: 20,
          wMm: 60,
          hMm: 80,
          style: {},
          zIndex: 1,
        },
        // Logo at bottom of sidebar
        {
          id: 'logo',
          kind: 'logo',
          xMm: 10,
          yMm: 260,
          wMm: 50,
          hMm: 20,
          style: {},
          zIndex: 2,
        },
        // Product name
        {
          id: 'product-name',
          kind: 'textField',
          xMm: 80,
          yMm: 20,
          wMm: 120,
          hMm: 18,
          style: {
            fontFamily: 'Kapra Neue',
            fontSize: 28,
            fontWeight: 'bold',
            color: '#1B3967',
            textTransform: 'uppercase',
          },
          binding: { metadataKey: 'title', fallbackValue: 'Produktnamn' },
          zIndex: 2,
        },
        // Subtitle/category
        {
          id: 'subtitle',
          kind: 'textField',
          xMm: 80,
          yMm: 40,
          wMm: 120,
          hMm: 8,
          style: {
            fontFamily: 'Arial',
            fontSize: 11,
            color: '#4A7BA7',
            fontWeight: 'bold',
          },
          binding: { metadataKey: 'varugrupp', fallbackValue: '' },
          zIndex: 2,
        },
        // Description box
        {
          id: 'desc-box',
          kind: 'rectangle',
          xMm: 80,
          yMm: 55,
          wMm: 120,
          hMm: 60,
          style: { backgroundColor: '#F5F5F5', borderRadius: 2 },
          zIndex: 1,
        },
        // Description text
        {
          id: 'description',
          kind: 'textField',
          xMm: 85,
          yMm: 60,
          wMm: 110,
          hMm: 50,
          style: {
            fontFamily: 'Arial',
            fontSize: 10,
            color: '#333333',
            lineHeight: 1.5,
          },
          binding: { metadataKey: 'produktbeskrivning', fallbackValue: 'Produktbeskrivning' },
          zIndex: 2,
        },
        // Info section header
        {
          id: 'info-header',
          kind: 'staticText',
          xMm: 80,
          yMm: 125,
          wMm: 120,
          hMm: 8,
          style: {
            fontFamily: 'Kapra Neue',
            fontSize: 14,
            color: '#1B3967',
            textTransform: 'uppercase',
          },
          content: 'Produktinformation',
          zIndex: 2,
        },
        // Separator
        {
          id: 'info-line',
          kind: 'line',
          xMm: 80,
          yMm: 135,
          wMm: 120,
          hMm: 0,
          style: { borderWidth: 0.5, borderColor: '#1B3967' },
          zIndex: 2,
        },
        // Article number
        {
          id: 'art-label',
          kind: 'staticText',
          xMm: 80,
          yMm: 140,
          wMm: 40,
          hMm: 6,
          style: { fontFamily: 'Arial', fontSize: 9, color: '#666666' },
          content: 'Artikelnummer',
          zIndex: 2,
        },
        {
          id: 'art-value',
          kind: 'textField',
          xMm: 130,
          yMm: 140,
          wMm: 70,
          hMm: 6,
          style: { fontFamily: 'Arial', fontSize: 9, fontWeight: 'bold', color: '#333333' },
          binding: { metadataKey: 'artikelnummer', fallbackValue: '' },
          zIndex: 2,
        },
        // EAN
        {
          id: 'ean-label',
          kind: 'staticText',
          xMm: 80,
          yMm: 150,
          wMm: 40,
          hMm: 6,
          style: { fontFamily: 'Arial', fontSize: 9, color: '#666666' },
          content: 'EAN',
          zIndex: 2,
        },
        {
          id: 'ean-value',
          kind: 'textField',
          xMm: 130,
          yMm: 150,
          wMm: 70,
          hMm: 6,
          style: { fontFamily: 'Arial', fontSize: 9, fontWeight: 'bold', color: '#333333' },
          binding: { metadataKey: 'ean', fallbackValue: '' },
          zIndex: 2,
        },
      ],
    },
  },
  {
    name: 'Vinblad Elegant',
    description: 'Elegant layout för vin med plats för beskrivning och detaljer',
    template: {
      type: 'wine',
      name: 'Vinblad Elegant',
      description: 'Elegant vinpresentation',
      isActive: true,
      showInAssetMenus: true,
      order: 0,
      page: {
        size: 'A4',
        orientation: 'portrait',
        marginMm: { top: 0, right: 0, bottom: 0, left: 0 },
        backgroundColor: '#FFFFFF',
      },
      elements: [
        // Top accent bar
        {
          id: 'top-bar',
          kind: 'rectangle',
          xMm: 0,
          yMm: 0,
          wMm: 210,
          hMm: 8,
          style: { backgroundColor: '#722F37' }, // Wine red
          zIndex: 0,
        },
        // Wine bottle image (tall, centered)
        {
          id: 'wine-image',
          kind: 'productImage',
          xMm: 20,
          yMm: 30,
          wMm: 60,
          hMm: 150,
          style: {},
          zIndex: 1,
        },
        // Wine name
        {
          id: 'wine-name',
          kind: 'textField',
          xMm: 90,
          yMm: 30,
          wMm: 105,
          hMm: 20,
          style: {
            fontFamily: 'Kapra Neue',
            fontSize: 26,
            fontWeight: 'bold',
            color: '#722F37',
          },
          binding: { metadataKey: 'title', fallbackValue: 'Vinnamn' },
          zIndex: 2,
        },
        // Region/Year
        {
          id: 'region',
          kind: 'textField',
          xMm: 90,
          yMm: 52,
          wMm: 105,
          hMm: 8,
          style: {
            fontFamily: 'Arial',
            fontSize: 11,
            color: '#666666',
            fontWeight: 'bold',
          },
          binding: { metadataKey: 'region', fallbackValue: '' },
          zIndex: 2,
        },
        // Description
        {
          id: 'description',
          kind: 'textField',
          xMm: 90,
          yMm: 70,
          wMm: 105,
          hMm: 60,
          style: {
            fontFamily: 'Arial',
            fontSize: 10,
            color: '#333333',
            lineHeight: 1.5,
          },
          binding: { metadataKey: 'produktbeskrivning', fallbackValue: '' },
          zIndex: 2,
        },
        // Details header
        {
          id: 'details-header',
          kind: 'staticText',
          xMm: 90,
          yMm: 140,
          wMm: 105,
          hMm: 8,
          style: {
            fontFamily: 'Kapra Neue',
            fontSize: 12,
            color: '#722F37',
            textTransform: 'uppercase',
          },
          content: 'Detaljer',
          zIndex: 2,
        },
        // Details line
        {
          id: 'details-line',
          kind: 'line',
          xMm: 90,
          yMm: 150,
          wMm: 105,
          hMm: 0,
          style: { borderWidth: 0.5, borderColor: '#722F37' },
          zIndex: 2,
        },
        // Article number
        {
          id: 'art-nr',
          kind: 'textField',
          xMm: 90,
          yMm: 155,
          wMm: 105,
          hMm: 6,
          style: { fontFamily: 'Arial', fontSize: 9, color: '#333333' },
          binding: { metadataKey: 'artikelnummer', prefix: 'Art.nr: ', fallbackValue: '' },
          zIndex: 2,
        },
        // Footer
        {
          id: 'footer',
          kind: 'rectangle',
          xMm: 0,
          yMm: 267,
          wMm: 210,
          hMm: 30,
          style: { backgroundColor: '#1B3967' },
          zIndex: 0,
        },
        // Logo
        {
          id: 'logo',
          kind: 'logo',
          xMm: 150,
          yMm: 272,
          wMm: 50,
          hMm: 20,
          style: {},
          zIndex: 3,
        },
      ],
    },
  },
];
